import React, { useState, useEffect } from "react";
import { safeFormat } from '@/lib/safeDate';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Plus, DollarSign, Calendar, Loader2, TrendingUp, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";

const stageColors = {
  lead: "bg-slate-100 text-slate-700 border-slate-200",
  qualification: "bg-blue-100 text-blue-700 border-blue-200",
  proposal: "bg-purple-100 text-purple-700 border-purple-200",
  negotiation: "bg-amber-100 text-amber-700 border-amber-200",
  closed_won: "bg-emerald-100 text-emerald-700 border-emerald-200",
  closed_lost: "bg-rose-100 text-rose-700 border-rose-200"
};

export default function DealsTab({ contactId }) {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (contactId) {
      loadDeals();
    }
  }, [contactId]);

  const loadDeals = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('deals')
        .select('*, items(id, unit_price, quantity, dependent_price, num_dependents, discount_percent, discount_amount)')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Fallback: calcular total dinâmico se total_amount está zerado mas tem items
      const dealsWithTotal = (data || []).map(deal => {
        if ((!deal.total_amount || deal.total_amount === 0) && deal.items?.length > 0) {
          const calculatedTotal = deal.items.reduce((sum, item) => {
            const baseTotal = (item.unit_price || 0) * (item.quantity || 1);
            const dependentTotal = (item.dependent_price || 0) * (item.num_dependents || 0);
            const subtotal = baseTotal + dependentTotal;
            const discount = item.discount_percent > 0 
              ? subtotal * (item.discount_percent / 100) 
              : (item.discount_amount || 0);
            return sum + (subtotal - discount);
          }, 0);
          return { ...deal, total_amount: calculatedTotal };
        }
        return deal;
      });
      
      setDeals(dealsWithTotal);
    } catch (error) {
      console.error("Error loading deals:", error);
      setDeals([]);
    }
    setIsLoading(false);
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount) return `${currency} 0.00`;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  const formatStage = (stage) => {
    if (!stage) return '';
    return stage.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleDealClick = (dealId) => {
    navigate(createPageUrl("DealDetail", { id: dealId }));
  };

  const handleCreateDeal = () => {
    navigate(createPageUrl("Deals", { action: 'create', contactId }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Negócios</h2>
          <p className="text-sm text-slate-500 mt-1">
            {deals.length} {deals.length === 1 ? 'negócio' : 'negócios'} no total
          </p>
        </div>
        <Button
          onClick={handleCreateDeal}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Negócio
        </Button>
      </div>

      {deals.length > 0 ? (
        <div className="space-y-4">
          {deals.map((deal) => (
            <Card 
              key={deal.id} 
              className="border-slate-200 hover:shadow-lg transition-all cursor-pointer group"
              onClick={() => handleDealClick(deal.id)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                        {deal.name || `Deal #${deal.id.slice(0, 8)}`}
                      </h3>
                      <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all mt-0.5" />
                    </div>
                    <Badge className={`${stageColors[deal.stage] || stageColors.lead} border font-medium`}>
                      {formatStage(deal.stage)}
                    </Badge>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 text-2xl font-bold text-slate-900">
                      <DollarSign className="w-6 h-6" />
                      {formatCurrency(deal.total_amount, deal.currency)}
                    </div>
                    {deal.win_probability !== null && deal.win_probability !== undefined && (
                      <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                        <TrendingUp className="w-4 h-4" />
                        {deal.win_probability}% probability
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Criado em</p>
                      <p className="font-medium">
                        {deal.created_at ? safeFormat(new Date(deal.created_at), "MMM dd, yyyy") : "N/A"}
                      </p>
                    </div>
                  </div>
                  {deal.expected_close_date && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Expected Close</p>
                        <p className="font-medium">
                          {safeFormat(new Date(deal.expected_close_date), "MMM dd, yyyy")}
                        </p>
                      </div>
                    </div>
                  )}
                  {deal.items && deal.items.length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <div>
                        <p className="text-xs text-slate-500">Items</p>
                        <p className="font-medium">{deal.items.length} item{deal.items.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  )}
                </div>

                {deal.notes && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-sm text-slate-600 line-clamp-2">{deal.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-slate-400">
          <DollarSign className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium mb-2">Nenhum negócio ainda</p>
          <p className="text-sm">Crie um negócio para acompanhar oportunidades com este contato</p>
          <Button
            onClick={handleCreateDeal}
            className="mt-6 bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Criar Primeiro Negócio
          </Button>
        </div>
      )}
    </div>
  );
}
